import { Injectable } from '@angular/core';
import { TableColumnsProp } from '../models/tables.model';
import {
  eventDetailsPropertyKeyDisplayProps,
  fullmanAndCbpGridFields,
  hyperlinkLabels,
  pageType,
  searchDataParam,
} from '../utility/constants';
import { IBasicSearchProps } from '../models/dynamic.model';
import { defauthawbPagination } from '../configs/common-configs';
import { Router } from '@angular/router';
import { HawbService } from 'src/app/features/service/hawb.service';
import { CommonService } from './common.service';
@Injectable({
  providedIn: 'root',
})
export class CommonTableService {
  constructor(
    private readonly router: Router,
    private readonly hawbService: HawbService,
    private readonly commonService: CommonService
  ) {}

  /* Display the selected columns in the table */
  public displaySelectedColumnsToTable(
    columnName: string,
    customiseTableColumns: TableColumnsProp[],
    displayedColumns: string[]
  ) {
    const isColumnView = customiseTableColumns.filter(
      data => data.key === columnName && data.isSelected
    );
    if (isColumnView && isColumnView.length > 0) return true;
    else {
      const columnIndex = displayedColumns.indexOf(columnName);
      if (columnIndex > -1) {
        displayedColumns.splice(columnIndex, 1);
      }
      return false;
    }
  }

  public isHyperlink(label: string, isFrom: string, key?: string) {
    const labelMap = {
      [fullmanAndCbpGridFields.fileName]: hyperlinkLabels.fullmanFetchFileName,
      [fullmanAndCbpGridFields.fullmanParsedDataId]:
        hyperlinkLabels.fullmanAction,
      [fullmanAndCbpGridFields.cbpRequestInfoId]: hyperlinkLabels.fullmanAction,
      [fullmanAndCbpGridFields.customCityClearanceJobId]:
        hyperlinkLabels.fullmanAction,
      [fullmanAndCbpGridFields.errorInfo]:
        hyperlinkLabels.fullmanFetchErrorInfo,
      [fullmanAndCbpGridFields.error]: hyperlinkLabels.fullmanFetchErrorInfo,
      [fullmanAndCbpGridFields.cbpStatusDescriptions]:
        hyperlinkLabels.cbpStatusDescription,
      [fullmanAndCbpGridFields.requestedEdi]: hyperlinkLabels.ediFileName,
      [fullmanAndCbpGridFields.responseEdi]: hyperlinkLabels.ediFileName,
      [fullmanAndCbpGridFields.responseInfo]: hyperlinkLabels.responseInfo,
      [pageType.smAirports]: hyperlinkLabels.smAirports,
      [pageType.smAirlines]: hyperlinkLabels.smAirlines,
      [pageType.hsCodes]: hyperlinkLabels.hsCode,
      [pageType.agent]: hyperlinkLabels.agent,
      [pageType.account]: hyperlinkLabels.account,
      [pageType.smRolesAndPermissions]: hyperlinkLabels.smRolesAndPermissions,
      [pageType.userManagement]: hyperlinkLabels.fullName,
      [pageType.smStatusCodes]: hyperlinkLabels.smStatusCodes,
      [pageType.hawb]: hyperlinkLabels.hawb,
      [pageType.bag]: hyperlinkLabels.bag,
      [pageType.manifest]: hyperlinkLabels.manifest,
      [pageType.mawb]: hyperlinkLabels.mawb,
      [pageType.serviceTypes]: hyperlinkLabels.serviceType,
      [pageType.agentTypes]: hyperlinkLabels.agentType,
      [pageType.events]: hyperlinkLabels.description,
      [pageType.serviceTypeGroups]: hyperlinkLabels.serviceTypeGroup,
      [pageType.clearanceTypes]: hyperlinkLabels.clearanceType,
      [pageType.smNumberRangesView]: hyperlinkLabels.range,
      [pageType.smNumberRangesSeriesName]: hyperlinkLabels.seriesName,
      [pageType.linkedAccounts]: hyperlinkLabels.actions,
      [pageType.facilities]: hyperlinkLabels.facility,
      [pageType.deliveryZone]: hyperlinkLabels.deliveryZone,
      [pageType.rateScales]: hyperlinkLabels.rateScales,
      [pageType.ibcChargeCodes]: hyperlinkLabels.ibcChargeCode,
      [pageType.apChargeCodes]: hyperlinkLabels.apChargeCodes,
      [pageType.accountsReceivables]: hyperlinkLabels.accountsReceivables,
      [pageType.apChargeCodeLink]: hyperlinkLabels.apChargeCodeLink,
      [pageType.accountsPayable]: hyperlinkLabels.accountsPayable,
      [pageType.currencyManagement]: hyperlinkLabels.currencyCode,
      [pageType.currencyManagementDetails]: hyperlinkLabels.currencyDetails,
      [pageType.apDashboardUnpublishedInvoices]:
        hyperlinkLabels.accountsReceivables,
      [pageType.apDashboardRecentInvoices]: hyperlinkLabels.accountsReceivables,
      [pageType.routing]: hyperlinkLabels.routing,
    };
    if (
      isFrom === pageType.fetchFullManData ||
      isFrom === pageType.customsCityDraftStatus ||
      isFrom === pageType.customsCityAbiStatus ||
      isFrom === pageType.cbpRequestStatus ||
      isFrom === pageType.cbpResponseStatus
    ) {
      if (key) {
        return label === labelMap[key];
      }
    } else if (isFrom === pageType.events) {
      if (key !== pageType.userManagement) {
        return label === labelMap[pageType.events];
      }
    } else if (isFrom === pageType.routing) {
      return key === 'origin' && label === labelMap[pageType.routing];
    } else {
      return label === labelMap[isFrom];
    }
    return false;
  }

  // Handle the hyperlink click for shipmentIdentifierValue
  public onShipmentIdentifierValueHyperlinkClick(event: any): void {
    const {
      shipmentIdentifier,
      shipmentIdentifierValue,
      shipmentId,
      shipmentIdOrMawbId,
      isFrom,
    } = event;
    const recordId = shipmentId ?? shipmentIdOrMawbId;
    if (!recordId) {
      return;
    }
    if (shipmentIdentifier === hyperlinkLabels.mawb) {
      this.navigateToMawbDetails(recordId, isFrom);
    } else if (
      shipmentIdentifier === hyperlinkLabels.hawb ||
      shipmentIdentifier ===
        eventDetailsPropertyKeyDisplayProps['agentShipmentTracker']
    ) {
      this.navigateToHawbDetails(shipmentIdentifierValue, recordId, isFrom);
    }
  }

  private navigateToMawbDetails(shipmentId: string, isFrom: string): void {
    this.router.navigate(['/mawb/mawb-information'], {
      queryParams: {
        mode: 'view',
        id: shipmentId,
        tab: pageType.apChargesForApModule === isFrom ? 'AP' : 'AR',
      },
    });
  }

  private navigateToHawbDetails(
    hawbNumber: string,
    shipmentId: string | undefined,
    isFrom: string
  ): void {
    // Set up navigation context data for proper back navigation
    const hawbSearchPayload: IBasicSearchProps = {
      paging: defauthawbPagination,
      searchText: '',
    };

    // Create hawb data object for navigation context
    const hawbData = {
      hawbExt: hawbNumber,
      hawbId: shipmentId || hawbNumber,
      id: shipmentId || hawbNumber,
    };

    // Set data in hawb service to preserve navigation context
    this.hawbService.setData({
      ...hawbSearchPayload,
      selectedHawbExt: hawbNumber,
      selectedHawbId: shipmentId || hawbNumber,
      hawbsList: [hawbData],
      totalData: 1,
      isAllSelected: false,
      totalDataPresent: 1,
    });

    // Clear any stored pagination props and set summary page context
    localStorage.setItem(searchDataParam.refreshPaginationProps, '');
    localStorage.setItem(searchDataParam.storedUrl, '');
    this.hawbService.isSummaryPage(searchDataParam.summary);

    // Navigate to HAWB information page
    const queryParams: any = {
      mode: 'view',
      tab: pageType.apChargesForApModule === isFrom ? 'AP' : 'AR',
    };

    if (shipmentId) {
      queryParams.id = shipmentId;
    } else {
      queryParams.id = hawbNumber;
    }

    this.router.navigate(['/hawb/hawb-information'], {
      queryParams,
    });
  }

  public calculateTotalChargeAmount(chargesData: any) {
    return chargesData.reduce((total: number, charge: any) => {
      const chargeAmount = this.commonService.isValidField(charge.chargeAmount)
        ? Number.parseFloat(charge.chargeAmount) || 0
        : 0;
      return total + chargeAmount;
    }, 0);
  }
}
